/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/A2/utils/a2ApiFormPrefill.ts.
 */

import type { FormDataResponse } from "../../../../../../interfaces/formInterfaces";
import { normalizeFuelKey } from "../../shared/fuelMixReferenceAutofill";

function s(v: unknown): string {
  if (v === null || v === undefined) return "";
  return String(v).trim();
}

/** Read vehicle row value from Prisma/API columns into CMS field names. */
export function readA2VehicleApiValueForField(
  fieldName: string,
  item: Record<string, unknown>
): string {
  if (fieldName === "tripsRequired") {
    return s(item.numTripsRequired ?? item.tripsRequired ?? "0");
  }
  if (fieldName === "totalDistanceTravelled") {
    return s(item.totalDistanceKm ?? item.totalDistanceTravelled ?? "0");
  }
  if (fieldName === "vehicleCapacity") {
    return s(item.capacity ?? item.vehicleCapacity ?? "");
  }
  if (fieldName === "vehicleCategory") {
    const direct =
      item.vehicleCategoryId ??
      item.vehicleCategoryEmissionFactorId ??
      item.vehicleCategory;
    if (direct !== null && direct !== undefined && direct !== "") {
      return s(direct);
    }
    const opt = item.vehicleCategoryOption as Record<string, unknown> | undefined;
    if (opt?.id != null) return s(opt.id);
    return s(item.vehicleCategory ?? "");
  }
  if (fieldName === "vehicle_rawMaterial_01") {
    const id = item.rawMaterialId ?? item.raw_material_id;
    return id != null && id !== "" ? s(id) : "";
  }

  const direct = item[fieldName];
  return direct !== null && direct !== undefined ? s(direct) : "";
}

function nestedFuelEnergy(item: Record<string, unknown>): Record<string, unknown> {
  return item?.energy && typeof item.energy === "object"
    ? (item.energy as Record<string, unknown>)
    : {};
}

/** Read fuel transport row value from Prisma/API columns into CMS field names. */
export function readA2FuelApiValueForField(
  fieldName: string,
  item: Record<string, unknown>
): string {
  const nestedEnergy = nestedFuelEnergy(item);

  if (fieldName === "fuelMix_01") {
    return s(item.fuelMix ?? item.fuelMixId ?? item.fuel_mix ?? "");
  }
  if (fieldName === "fuelQuantity") {
    return s(item.quantity ?? item.fuelQuantity ?? "");
  }
  if (fieldName === "fuelQuantity_unit") {
    const u = item.quantityUnitId ?? item.fuelQuantityUnitId ?? item.unitId;
    return u != null && String(u).trim() !== "" ? String(u) : "";
  }
  if (fieldName === "fuelDensity") {
    return s(item.fuelDensity ?? "");
  }
  if (fieldName === "fuelDensity_unit") {
    const u = item.fuelDensityUnitId;
    return u != null && String(u).trim() !== "" ? String(u) : "";
  }
  if (fieldName === "calorificValue") {
    return s(item.calorificValue ?? "");
  }
  if (fieldName === "calorificValue_unit") {
    const u = item.calorificValueUnitId;
    return u != null && String(u).trim() !== "" ? String(u) : "";
  }
  if (fieldName === "embodiedEnergy") {
    return s(item.embodiedEnergy ?? nestedEnergy.embodiedEnergy ?? "");
  }
  if (fieldName === "embodiedEnergy_unit") {
    const u =
      nestedEnergy.embodiedEnergyUnitId ?? item.embodiedEnergyUnitId;
    return u != null && String(u).trim() !== "" ? String(u) : "";
  }

  const direct = item[fieldName];
  return direct !== null && direct !== undefined ? s(direct) : "";
}

/** Map stored fuel mix (label or legacy token) to CMS dropdown option value. */
export function normalizeA2FuelMixInValues(
  vals: Record<string, string>,
  formData: FormDataResponse | null,
  fuelTransportCode = "A2_FUEL_TRANSPORT",
  fuelMixFieldName = "fuelMix_01"
): Record<string, string> {
  if (!formData) return vals;

  const fuelSec = formData.formSubSections?.find(
    (sec) => sec.code === fuelTransportCode
  );
  const field = fuelSec?.fields?.find((f) => f.name === fuelMixFieldName);
  const opts = field?.dropdown?.options || field?.dropdownValues || [];
  if (!opts.length) return vals;

  const prefix = `${fuelMixFieldName}_${fuelTransportCode}_row`;
  let changed = false;
  const next = { ...vals };

  for (const key of Object.keys(vals)) {
    if (!key.startsWith(prefix)) continue;
    const raw = String(vals[key] ?? "").trim();
    if (!raw) continue;
    if (opts.some((o) => String(o.value ?? "") === raw)) continue;

    const rawNorm = normalizeFuelKey(raw);
    const hit = opts.find((o) => {
      const val = String(o.value ?? "").trim();
      const lbl = String(o.label ?? "").trim();
      if (val === raw || lbl === raw) return true;
      const valNorm = normalizeFuelKey(val);
      const lblNorm = normalizeFuelKey(lbl);
      return (
        (rawNorm && (valNorm === rawNorm || lblNorm === rawNorm)) ||
        lbl.toLowerCase() === raw.toLowerCase()
      );
    });

    if (hit?.value != null && String(hit.value).trim() !== "") {
      next[key] = String(hit.value);
      changed = true;
    }
  }

  return changed ? next : vals;
}
